import React, { useRef, forwardRef, useImperativeHandle } from 'react';
import jsPDF from 'jspdf';
import 'jspdf-font';

// Türkçe karakter desteği için font yükleme
import { addFont } from 'jspdf-font';

// Türkçe karakterleri düzgün göstermek için yardımcı fonksiyon
const encodeTurkishText = (text) => {
  if (!text) return '';
  
  // Türkçe karakterleri UTF-8 encoding ile düzgün göstermek için
  return text
    .replace(/İ/g, 'I')  // Büyük İ harfini I yap
    .replace(/ı/g, 'i'); // Küçük ı harfini i yap
};

// Türkçe karakterleri düzgün göstermek için gelişmiş fonksiyon
const fixTurkishChars = (text) => {
  if (!text) return '';
  
  const turkishChars = {
    'ğ': 'g', 'Ğ': 'G',
    'ü': 'u', 'Ü': 'U',
    'ş': 's', 'Ş': 'S',
    'ı': 'i', 'I': 'I',
    'ö': 'o', 'Ö': 'O',
    'ç': 'c', 'Ç': 'C',
    'İ': 'I',
  };
  
  return text.replace(/[ğĞüÜşŞıIöÖçÇİı]/g, (match) => turkishChars[match] || match);
};

// Değerleri string'e çeviren yardımcı fonksiyon
const ensureString = (value) => {
  if (value === null || value === undefined) return '';
  return String(value);
};

const CasePdfGenerator = forwardRef(({ caseData, onGenerate, transportTypes = [] }, ref) => {
  const pdfRef = useRef(null);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      timeZone: 'Europe/Istanbul'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '-';
    return timeString.substring(0, 5); // HH:MM format
  };

  const getStatusInfo = (status) => {
    const STATUS_OPTIONS = [
      { value: 'initiated', label: 'Baslatildi', color: '#3B82F6' },
      { value: 'in_progress', label: 'Devam Ediyor', color: '#F59E0B' },
      { value: 'completed', label: 'Tamamlandi', color: '#10B981' },
      { value: 'cancelled', label: 'Iptal Edildi', color: '#EF4444' }
    ];
    return STATUS_OPTIONS.find(option => option.value === status) || STATUS_OPTIONS[0];
  };

  const getTransportTypeLabel = (type) => {
    // transportTypes prop'u CaseDetailPage'den gelir (veritabanından çekilen liste)
    // Prop boşsa veya yüklenmemişse value'yu olduğu gibi döndür
    if (transportTypes && transportTypes.length > 0) {
      return transportTypes.find(option => option.value === type)?.label || type;
    }
    return type;
  };

  // Helper function to check if stage 3 has any data
  const hasStage3Data = (caseData) => {
    if (!caseData) return false;
    
    return !!(
      caseData.notification_time || caseData.arrival_time || caseData.delivery_time ||
      caseData.ambulance_plate || caseData.staff_1_name || caseData.staff_2_name || 
      caseData.staff_3_name || caseData.handover_person || caseData.handover_title || 
      caseData.special_notes || caseData.result_description || caseData.completion_notes
    );
  };

  const generatePDF = async () => {
    if (!pdfRef.current) return;

    try {
      onGenerate && onGenerate('generating');

      // PDF oluştur - Türkçe karakter desteği ile
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Türkçe karakter desteği için özel font ayarları
      pdf.setFont('helvetica');
      pdf.setFontSize(12);
      
      // Türkçe karakterleri düzgün göstermek için encoding ayarı
      pdf.setLanguage('tr');
      
      let yPosition = 20;
      const pageWidth = 210;
      const margin = 20;
      
             // Header
       pdf.setFontSize(18);
       pdf.setFont('helvetica', 'bold');
       pdf.text('EMT AMBULANS CRM', pageWidth / 2, yPosition, { align: 'center' });
       yPosition += 10;
       
       pdf.setFontSize(14);
       pdf.text('Vaka Detayi', pageWidth / 2, yPosition, { align: 'center' });
       yPosition += 8;
       
               pdf.setFontSize(12);
        pdf.text(`Vaka #${caseData.case_number} - ${formatDate(caseData.request_date)}`, pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 12;
        
        // Status - Daha belirgin ve yukarıda
        const statusInfo = getStatusInfo(caseData.status);
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`Durum: ${statusInfo.label}`, pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 8;
        
        // Progress
        const progress = caseData.status === 'initiated' ? 33 : 
                        caseData.status === 'in_progress' ? 66 : 100;
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Ilerleme: %${progress}`, pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 15;
       
       // Stage 1
       pdf.setFontSize(14);
       pdf.setFont('helvetica', 'bold');
       pdf.text('1. Asama: Vaka Baslatma', margin, yPosition);
       yPosition += 8;
       
       pdf.setFontSize(10);
       pdf.setFont('helvetica', 'normal');
      
                                                       // Stage 1 details
                   const stage1Details = [
            { label: 'Talep Tarihi:', value: ensureString(formatDate(caseData.request_date)) },
            { label: 'Cagri Saati:', value: ensureString(formatTime(caseData.request_time)) },
            { label: 'Hasta:', value: ensureString(`${fixTurkishChars(caseData.patient_name || '')} ${fixTurkishChars(caseData.patient_surname || '')}`) },
            caseData.patient_age && { label: 'Yas:', value: ensureString(caseData.patient_age) },
            caseData.patient_birth_year && { label: 'Dogum Yili:', value: ensureString(caseData.patient_birth_year) },
            caseData.patient_address && { label: 'Hasta Adresi:', value: ensureString(fixTurkishChars(caseData.patient_address)) },
            caseData.patient_address_details && { label: 'Adres Detayi:', value: ensureString(fixTurkishChars(caseData.patient_address_details)) },
            caseData.initial_description && { label: 'Aciklama:', value: ensureString(fixTurkishChars(caseData.initial_description)) },
            caseData.call_giver_authority && { label: 'Cagriyi Veren:', value: ensureString(fixTurkishChars(caseData.call_giver_authority)) },
            caseData.requesting_authority && { label: 'Vakayi Alan:', value: ensureString(fixTurkishChars(caseData.requesting_authority)) }
          ].filter(Boolean);
       
       stage1Details.forEach(detail => {
         if (yPosition > 250) {
           pdf.addPage();
           yPosition = 20;
         }
         
         // Label'ı koyu fontta yaz
         pdf.setFont('helvetica', 'bold');
         pdf.text(detail.label, margin, yPosition);
         
         // Value'yu normal fontta yaz
         pdf.setFont('helvetica', 'normal');
         const labelWidth = pdf.getTextWidth(detail.label);
         pdf.text(detail.value, margin + labelWidth + 5, yPosition);
         
         yPosition += 6;
       });
      
      yPosition += 10;
      
      // Stage 2
      if (caseData.status !== 'initiated') {
        if (yPosition > 250) {
          pdf.addPage();
          yPosition = 20;
        }
        
                                   pdf.setFontSize(14);
          pdf.setFont('helvetica', 'bold');
          pdf.text('2. Asama: Nakil Bilgileri', margin, yPosition);
          yPosition += 8;
          
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'normal');
        
                                                                                                           const stage2Details = [
             caseData.team_name && { label: 'Atanan Ekip:', value: ensureString(fixTurkishChars(caseData.team_name)) },
             caseData.transport_type && { label: 'Sevk Turu:', value: ensureString(fixTurkishChars(getTransportTypeLabel(caseData.transport_type))) },
             caseData.protocol_number && { label: 'ASOS Protokol No:', value: ensureString(caseData.protocol_number) },
             caseData.transport_payment_type && { label: 'Nakil Turu:', value: caseData.transport_payment_type === 'paid' ? 'Ucretli Nakil' : 'Ucretsiz Nakil' },
             caseData.transport_payment_type === 'paid' && caseData.transport_fee && { label: 'Ucret:', value: `${ensureString(caseData.transport_fee)} TL` },
             caseData.destination_address && { label: 'Gidecegi Adres:', value: ensureString(fixTurkishChars(caseData.destination_address)) },
             caseData.billing_institution && { label: 'Faturalanan Kurum:', value: ensureString(fixTurkishChars(caseData.billing_institution)) },
             caseData.notification_time && { label: 'Bildirim Saati:', value: ensureString(formatTime(caseData.notification_time)) }
           ].filter(Boolean);
         
         stage2Details.forEach(detail => {
           if (yPosition > 250) {
             pdf.addPage();
             yPosition = 20;
           }
           
           // Label'ı koyu fontta yaz
           pdf.setFont('helvetica', 'bold');
           pdf.text(detail.label, margin, yPosition);
           
           // Value'yu normal fontta yaz
           pdf.setFont('helvetica', 'normal');
           const labelWidth = pdf.getTextWidth(detail.label);
           pdf.text(detail.value, margin + labelWidth + 5, yPosition);
           
           yPosition += 6;
         });
        
        yPosition += 10;
      }
      
      // Stage 3
      if (hasStage3Data(caseData)) {
        if (yPosition > 250) {
          pdf.addPage();
          yPosition = 20;
        }
        
                                   pdf.setFontSize(14);
          pdf.setFont('helvetica', 'bold');
          pdf.text('3. Asama: Tamamlama', margin, yPosition);
          yPosition += 8;
          
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'normal');
        
                                                                       const stage3Details = [
             caseData.notification_time && { label: 'Nakil Ekibe Bildirim:', value: ensureString(formatTime(caseData.notification_time)) },
             caseData.arrival_time && { label: 'Vakaya Ulasim:', value: ensureString(formatTime(caseData.arrival_time)) },
             caseData.delivery_time && { label: 'Vaka Teslim:', value: ensureString(formatTime(caseData.delivery_time)) },
             caseData.ambulance_plate && { label: 'Ambulans Plakasi:', value: ensureString(caseData.ambulance_plate) },
             caseData.staff_1_name && { label: 'Personel 1:', value: ensureString(fixTurkishChars(caseData.staff_1_name)) },
             caseData.staff_2_name && { label: 'Personel 2:', value: ensureString(fixTurkishChars(caseData.staff_2_name)) },
             caseData.staff_3_name && { label: 'Personel 3:', value: ensureString(fixTurkishChars(caseData.staff_3_name)) },
             caseData.handover_person && { label: 'Teslim Alan:', value: ensureString(fixTurkishChars(caseData.handover_person)) },
             caseData.handover_title && { label: 'Teslim Alan Unvan:', value: ensureString(fixTurkishChars(caseData.handover_title)) },
             caseData.result_description && { label: 'Sonuc:', value: ensureString(fixTurkishChars(caseData.result_description)) },
             caseData.completion_notes && { label: 'Tamamlama Notlari:', value: ensureString(fixTurkishChars(caseData.completion_notes)) },
             caseData.special_notes && { label: 'Ozel Notlar:', value: ensureString(fixTurkishChars(caseData.special_notes)) }
           ].filter(Boolean);
         
         stage3Details.forEach(detail => {
           if (yPosition > 250) {
             pdf.addPage();
             yPosition = 20;
           }
           
           // Label'ı koyu fontta yaz
           pdf.setFont('helvetica', 'bold');
           pdf.text(detail.label, margin, yPosition);
           
           // Value'yu normal fontta yaz
           pdf.setFont('helvetica', 'normal');
           const labelWidth = pdf.getTextWidth(detail.label);
           pdf.text(detail.value, margin + labelWidth + 5, yPosition);
           
           yPosition += 6;
         });
      }
      
             // Footer kaldırıldı
      
      const fileName = `Vaka_${caseData.case_number}_${formatDate(caseData.request_date)}.pdf`;
      pdf.save(fileName);

      onGenerate && onGenerate('success');
    } catch (error) {
      console.error('PDF oluşturma hatası:', error);
      onGenerate && onGenerate('error', error.message);
    }
  };

  // Expose generatePDF method to parent component
  useImperativeHandle(ref, () => ({
    generatePDF
  }), []);

  const statusInfo = getStatusInfo(caseData.status);
  const progress = caseData.status === 'initiated' ? 33 : 
                  caseData.status === 'in_progress' ? 66 : 100;

  return (
    <div className="hidden">
      <div ref={pdfRef} className="bg-white p-8 max-w-4xl mx-auto" style={{ fontFamily: 'Arial, sans-serif' }}>
        {/* Header */}
        <div className="text-center mb-8 border-b-2 border-gray-300 pb-4">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-blue-600 text-white px-4 py-2 rounded-lg mr-4 font-bold">
              EMT AMBULANS
            </div>
            <div className="bg-gray-100 text-gray-800 px-4 py-2 rounded-lg font-semibold">
              EMT AMBULANS CRM
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Vaka Detayı</h1>
          <p className="text-lg text-gray-600">
            Vaka #{caseData.case_number} - {formatDate(caseData.request_date)}
          </p>
          <div className="mt-4">
            <span 
              className="inline-block px-4 py-2 rounded-full text-white font-semibold"
              style={{ backgroundColor: statusInfo.color }}
            >
              {statusInfo.label}
            </span>
          </div>
        </div>

        {/* Progress */}
        <div className="mb-8 p-6 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <span className="text-lg font-semibold text-gray-700">Vaka İlerlemesi</span>
            <span className="text-lg font-semibold text-gray-600">{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-4">
            <div 
              className="bg-blue-600 h-4 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <div className="flex justify-between mt-4 text-sm text-gray-600">
            <span>Başlatıldı</span>
            <span>Nakil Bilgileri</span>
            <span>Tamamlandı</span>
          </div>
        </div>

        {/* Case Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Stage 1: Vaka Başlatma */}
          <div className="border border-gray-300 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center mr-3">
                1
              </div>
              <h2 className="text-xl font-bold text-gray-900">1. Aşama: Vaka Başlatma</h2>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-semibold text-gray-600 mb-1">Talep Tarihi</p>
                  <p className="text-lg font-semibold text-gray-900">{formatDate(caseData.request_date)}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-600 mb-1">Çağrı Saati</p>
                  <p className="text-lg font-semibold text-gray-900">{formatTime(caseData.request_time)}</p>
                </div>
              </div>
              
              <div>
                <p className="text-sm font-semibold text-gray-600 mb-1">Hasta</p>
                <p className="text-lg font-semibold text-gray-900">
                  {caseData.patient_name} {caseData.patient_surname}
                </p>
                {(caseData.patient_age || caseData.patient_birth_year) && (
                  <p className="text-sm text-gray-600">
                    {caseData.patient_age && `${caseData.patient_age} yaş`}
                    {caseData.patient_birth_year && ` (${caseData.patient_birth_year})`}
                  </p>
                )}
              </div>
              
              {caseData.patient_address && (
                <div>
                  <p className="text-sm font-semibold text-gray-600 mb-1">Hasta Adresi</p>
                  <p className="text-lg text-gray-900">{caseData.patient_address}</p>
                  {caseData.patient_address_details && (
                    <p className="text-sm text-gray-600 mt-1">
                      <strong>Detay:</strong> {caseData.patient_address_details}
                    </p>
                  )}
                </div>
              )}
              
              {caseData.initial_description && (
                <div>
                  <p className="text-sm font-semibold text-gray-600 mb-1">Açıklama</p>
                  <p className="text-lg text-gray-900">{caseData.initial_description}</p>
                </div>
              )}
              
              {caseData.call_giver_authority && (
                <div>
                  <p className="text-sm font-semibold text-gray-600 mb-1">Çağrıyı Veren Yetkili</p>
                  <p className="text-lg text-gray-900">{caseData.call_giver_authority}</p>
                </div>
              )}
              
              {caseData.requesting_authority && (
                <div>
                  <p className="text-sm font-semibold text-gray-600 mb-1">Vakayı Alan Yetkili</p>
                  <p className="text-lg text-gray-900">{caseData.requesting_authority}</p>
                </div>
              )}
            </div>
          </div>

          {/* Stage 2: Nakil Bilgileri */}
          <div className="border border-gray-300 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center mr-3">
                2
              </div>
              <h2 className="text-xl font-bold text-gray-900">2. Aşama: Nakil Bilgileri</h2>
            </div>
            
            <div className="space-y-4">
              {caseData.status === 'initiated' ? (
                <div className="text-center text-gray-500 py-8">
                  <p>2. aşama henüz tamamlanmamış</p>
                </div>
              ) : (
                <>
                  {caseData.team_name && (
                    <div>
                      <p className="text-sm font-semibold text-gray-600 mb-1">Atanan Ekip</p>
                      <p className="text-lg font-semibold text-gray-900">{caseData.team_name}</p>
                    </div>
                  )}
                  
                  {caseData.transport_type && (
                    <div>
                      <p className="text-sm font-semibold text-gray-600 mb-1">Sevk Türü</p>
                      <p className="text-lg text-gray-900">{getTransportTypeLabel(caseData.transport_type)}</p>
                    </div>
                  )}
                  
                  {caseData.protocol_number && (
                    <div>
                      <p className="text-sm font-semibold text-gray-600 mb-1">ASOS Protokol No</p>
                      <p className="text-lg font-semibold text-gray-900">{caseData.protocol_number}</p>
                    </div>
                  )}
                  
                  {caseData.transport_payment_type && (
                    <div>
                      <p className="text-sm font-semibold text-gray-600 mb-1">Nakil Türü</p>
                      <p className="text-lg text-gray-900">
                        {caseData.transport_payment_type === 'paid' ? 'Ücretli Nakil' : 'Ücretsiz Nakil'}
                      </p>
                      {caseData.transport_payment_type === 'paid' && caseData.transport_fee && (
                        <p className="text-lg font-semibold text-green-600">{caseData.transport_fee} TL</p>
                      )}
                    </div>
                  )}
                  
                  {caseData.destination_address && (
                    <div>
                      <p className="text-sm font-semibold text-gray-600 mb-1">Gideceği Adres</p>
                      <p className="text-lg text-gray-900">{caseData.destination_address}</p>
                    </div>
                  )}
                  
                  {caseData.billing_institution && (
                    <div>
                      <p className="text-sm font-semibold text-gray-600 mb-1">Faturalanan Kurum</p>
                      <p className="text-lg text-gray-900">{caseData.billing_institution}</p>
                    </div>
                  )}
                  
                  {caseData.notification_time && (
                    <div>
                      <p className="text-sm font-semibold text-gray-600 mb-1">Nakil Ekibe Bildirim Saati</p>
                      <p className="text-lg font-semibold text-gray-900">{formatTime(caseData.notification_time)}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Stage 3: Tamamlama - Full Width */}
        <div className="mt-8 border border-gray-300 rounded-lg p-6">
          <div className="flex items-center mb-6">
            <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center mr-3">
              3
            </div>
            <h2 className="text-xl font-bold text-gray-900">3. Aşama: Tamamlama</h2>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Zaman Bilgileri */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Zaman Bilgileri</h3>
              
              {caseData.notification_time && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm font-semibold text-blue-600 mb-1">Nakil Ekibe Bildirim</p>
                  <p className="text-xl font-bold text-blue-900">{formatTime(caseData.notification_time)}</p>
                </div>
              )}
              
              {caseData.arrival_time && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm font-semibold text-green-600 mb-1">Vakaya Ulaşım</p>
                  <p className="text-xl font-bold text-green-900">{formatTime(caseData.arrival_time)}</p>
                </div>
              )}
              
              {caseData.delivery_time && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <p className="text-sm font-semibold text-purple-600 mb-1">Vaka Teslim</p>
                  <p className="text-xl font-bold text-purple-900">{formatTime(caseData.delivery_time)}</p>
                </div>
              )}
            </div>

            {/* Ekip Bilgileri */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Ekip Bilgileri</h3>
              
              {caseData.ambulance_plate && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-sm font-semibold text-gray-600 mb-1">Ambulans Plakası</p>
                  <p className="text-xl font-bold text-gray-900">{caseData.ambulance_plate}</p>
                </div>
              )}
              
              {(caseData.staff_1_name || caseData.staff_2_name || caseData.staff_3_name) && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm font-semibold text-green-600 mb-3">Görevli Personel</p>
                  <div className="space-y-2">
                    {caseData.staff_1_name && (
                      <div className="flex items-center space-x-3">
                        <div className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-bold">1</div>
                        <p className="font-semibold text-green-900">{caseData.staff_1_name}</p>
                      </div>
                    )}
                    {caseData.staff_2_name && (
                      <div className="flex items-center space-x-3">
                        <div className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-bold">2</div>
                        <p className="font-semibold text-green-900">{caseData.staff_2_name}</p>
                      </div>
                    )}
                    {caseData.staff_3_name && (
                      <div className="flex items-center space-x-3">
                        <div className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-bold">3</div>
                        <p className="font-semibold text-green-900">{caseData.staff_3_name}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Teslim ve Sonuç Bilgileri */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Teslim ve Sonuç</h3>
              
              {(caseData.handover_person || caseData.handover_title) && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <p className="text-sm font-semibold text-purple-600 mb-2">Teslim Bilgileri</p>
                  {caseData.handover_person && (
                    <div className="mb-2">
                      <p className="text-xs text-purple-600 font-medium">Teslim Alan Kişi</p>
                      <p className="font-semibold text-purple-900">{caseData.handover_person}</p>
                    </div>
                  )}
                  {caseData.handover_title && (
                    <div>
                      <p className="text-xs text-purple-600 font-medium">Unvan</p>
                      <p className="font-semibold text-purple-900">{caseData.handover_title}</p>
                    </div>
                  )}
                </div>
              )}
              
              {(caseData.result_description || caseData.completion_notes) && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm font-semibold text-green-600 mb-2">Sonuç Bilgileri</p>
                  {caseData.result_description && (
                    <div className="mb-2">
                      <p className="text-xs text-green-600 font-medium">Sonuç</p>
                      <p className="text-sm text-green-800">{caseData.result_description}</p>
                    </div>
                  )}
                  {caseData.completion_notes && (
                    <div>
                      <p className="text-xs text-green-600 font-medium">Tamamlama Notları</p>
                      <p className="text-sm text-green-800">{caseData.completion_notes}</p>
                    </div>
                  )}
                </div>
              )}
              
              {caseData.special_notes && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm font-semibold text-yellow-600 mb-2">Özel Notlar</p>
                  <p className="text-sm text-yellow-800">{caseData.special_notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t-2 border-gray-300 text-center">
          <p className="text-sm text-gray-600">
            Bu belge EMT Ambulans CRM sistemi tarafından {new Date().toLocaleDateString('tr-TR')} tarihinde oluşturulmuştur.
          </p>
        </div>
      </div>
    </div>
  );
});

export default CasePdfGenerator; 